import { Injectable } from '@angular/core';
import dexFreight, { Microservices } from '@microservices';
import { Subscription } from 'rxjs';
import { UserService } from './user.service';
import { CompanySettingsService } from './company-settings.service';
import { CacheListener } from './@utils/cache-listener';
import { CompanyService } from './company.service';
import { CompanyDocumentsResponse } from './interfaces';
import { missingRequiredDocuments } from './documents.logic';

@Injectable({
  providedIn: 'root'
})
export class DocumentsService {

  public companyDocuments: CacheListener<CompanyDocumentsResponse> = new CacheListener("company-documents", null, this.syncCompanyDocuments.bind(this));
  public hasCompanyRequiredDocsForBidding: CacheListener<boolean> = new CacheListener("has-company-required-docs-for-bidding", null, this.syncHasCompanyRequiredDocsForBidding.bind(this));
  public docsEnum: Array<string> = ["w9", "insurance", "authority", "tax_constancy", "terms_and_conditions", "default_t&c"]

  private listeners: Subscription[] = [];

  constructor(
    private userService: UserService,
    private companyService: CompanyService,
    private micro: Microservices,
    private companySettingsService: CompanySettingsService
  ) { }

  setupListeners() {
    this.listeners.push(
      this.companyDocuments.syncWhenChanges(this.companyService.company),
      this.hasCompanyRequiredDocsForBidding.syncWhenChanges(this.companyDocuments),
      this.hasCompanyRequiredDocsForBidding.syncWhenPropertiesChanges(
        this.companySettingsService.companySettings,
        ['publicShipment']
      ),

      this.companyDocuments.bind(this.companyService.company),
      this.hasCompanyRequiredDocsForBidding.bind(this.companyDocuments)
    );
  }

  removeListeners() {
    this.listeners.forEach(listener => listener.unsubscribe());
  }

  //* Syncs everything
  async syncCompanyDocuments (): Promise<CompanyDocumentsResponse> {
    const user = await this.userService.user.get();

    return await dexFreight.Documents.getCompanyDocuments(user.companyId, localStorage.token) as unknown as CompanyDocumentsResponse;
  }

  //* Syncs if the user has the required documents to accept or decline bids
  async syncHasCompanyRequiredDocsForBidding(): Promise<boolean> {
    const user = await this.userService.user.get();
    const role = user.role;

    try {
      let doc = [];
      let { groups } = await this.companyDocuments.get();

      for (let docHub of groups) {
        if (docHub.docs.length && this.docsEnum.includes(docHub.type)) {
          doc.push(docHub.type);
        }
      }

      if (role === "shipper") return true;

      return await this.hasRequiredDocs(doc);
    } catch (e) {
      console.log(e);
      return role === "shipper";
    }
  }

  /** Determines if the user has the required documents to accept or decline bids */
  private async hasRequiredDocs(doc: string[]) {
    const user = await this.userService.user.get();
    const role = user.role;

    return missingRequiredDocuments(role, doc).length === 0;
  }
}
